import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import type { DomainCheck, Lead, LeadInput, OfferedPlan } from '@/api/types';

/** Extensions we register for customers (same list as the API). */
export const DOMAIN_TLDS = ['app', 'com.br', 'com', 'fit', 'pro'] as const;
export type DomainTld = (typeof DOMAIN_TLDS)[number];

export function useDomainCheck(name: string, tld: DomainTld) {
  return useQuery({
    queryKey: ['landing', 'domain', name, tld],
    queryFn: () =>
      api.get<DomainCheck>('/landing/domain-check', { auth: false, query: { name, tld } }),
    enabled: /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(name),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function useSendLead() {
  return useMutation({
    mutationFn: (body: LeadInput) => api.post('/landing/leads', body, { auth: false }),
  });
}

// ── Console ──────────────────────────────────────────────────────────────────

export function useLeads(status?: Lead['status']) {
  return useQuery({
    queryKey: ['admin', 'leads', status ?? 'all'],
    queryFn: () => api.get<Lead[]>('/admin/leads', { query: { status } }),
  });
}

export function useUpdateLead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; status?: Lead['status']; notes?: string }) =>
      api.patch<Lead>(`/admin/leads/${id}`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin', 'leads'] }),
  });
}

export function usePublicPlans() {
  return useQuery({
    queryKey: ['public', 'plans'],
    queryFn: () => api.get<OfferedPlan[]>('/public/plans', { auth: false }),
    staleTime: 5 * 60_000,
  });
}
